import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import AgentAvatar from '../pages/AgentAvatar';
import InlineAgentCard, { AGENT_MODEL_LABELS } from '../pages/InlineAgentCard';
import NodeAutocomplete from './NodeAutocomplete';
import { stopPropagation } from './nodeUiStyles';

/**
 * NodeAgentPicker — searchable agent selector with avatar-rich dropdown options
 * and optional InlineAgentCard summaries. Supports single or multiple selection;
 * when `showCard` is set, selected agents render as compact cards below the field
 * (with optional order badges when `ordered` is true).
 *
 * Pass `agents` from the caller (typically filtered non-system agents from
 * GET project-service/agent/list). Supports `inDialog` / `canvas` like other
 * NodeUI pickers. On canvas, `collapseOnSelect` (default true) collapses to a
 * card-only view after pick; set false to keep the dropdown and card together.
 */
export function renderAgentPickerOption(props, option) {
  if (!option?.agentId) return null;

  const modelLabel = AGENT_MODEL_LABELS[option.model] || option.model || 'No model';
  const toolCount = option.enabledTools ? option.enabledTools.length : 0;

  return (
    <li {...props} key={option.agentId}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', py: 0.5 }}>
        <AgentAvatar
          name={option.name}
          size={32}
          borderRadius="8px"
          systemAgent={option.systemAgent}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{ fontSize: '13px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}
            noWrap
          >
            {option.name || 'Untitled Agent'}
          </Typography>
          <Typography
            sx={{ fontSize: '11.5px', color: 'text.secondary', lineHeight: 1.3 }}
            noWrap
          >
            {option.description || 'No description'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
              {modelLabel}
            </Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'divider' }}>·</Typography>
            <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
              {toolCount > 0 ? `${toolCount} tool${toolCount !== 1 ? 's' : ''}` : 'No tools'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </li>
  );
}

const NodeAgentPicker = React.memo(function NodeAgentPicker({
  label = 'Agent',
  value = null,
  onChange,
  agents = [],
  loading = false,
  loadingText = 'Loading agents...',
  noOptionsText = 'No agents found',
  placeholder = 'Select an agent...',
  disabled = false,
  inDialog = false,
  canvas = false,
  /** When false on canvas, keep the searchable picker visible after selection (card still shows below). */
  collapseOnSelect = true,
  multiple = false,
  ordered = false,
  showCard = true,
  compactCard = true,
  onConfigure,
  renderOption = renderAgentPickerOption,
  getOptionLabel = (option) => option?.name || 'Untitled',
}) {
  const selectedAgents = multiple
    ? (Array.isArray(value) ? value : [])
    : (value ? [value] : []);
  const autocompleteValue = multiple ? (Array.isArray(value) ? value : []) : value;
  const hideInputTags = multiple && showCard;
  const resolvedPlaceholder =
    multiple && hideInputTags && selectedAgents.length > 0
      ? `${selectedAgents.length} agent${selectedAgents.length !== 1 ? 's' : ''} selected`
      : multiple
        ? 'Select agents...'
        : placeholder;
  const collapseOnCanvas = canvas && !multiple && showCard && collapseOnSelect;
  const [pickerExpanded, setPickerExpanded] = useState(!value);
  const showCollapsedCard = collapseOnCanvas && value && !pickerExpanded;

  useEffect(() => {
    if (!collapseOnCanvas) return;
    if (value) {
      setPickerExpanded(false);
    } else {
      setPickerExpanded(true);
    }
  }, [collapseOnCanvas, value?.agentId]);

  const handleChange = useCallback((nextValue) => {
    onChange?.(nextValue);
    if (collapseOnCanvas && nextValue) {
      setPickerExpanded(false);
    }
  }, [collapseOnCanvas, onChange]);

  const handleShowPicker = useCallback((event) => {
    event.stopPropagation();
    setPickerExpanded(true);
  }, []);

  const renderSelectedCard = (agent, index) => (
    <Box
      key={agent.agentId}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: ordered ? 1 : 0,
        minWidth: 0,
      }}
    >
      {ordered ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 22,
            height: 22,
            mt: 1.25,
            borderRadius: '6px',
            backgroundColor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
            {index + 1}
          </Typography>
        </Box>
      ) : null}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <InlineAgentCard
          agent={agent}
          compact={multiple || compactCard}
          onConfigure={onConfigure}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {showCollapsedCard ? (
        <Box
          className="nodrag nopan nowheel"
          onMouseDown={stopPropagation}
          onClick={stopPropagation}
          onPointerDown={stopPropagation}
        >
          {label ? (
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'text.secondary',
                lineHeight: 1.4,
                mb: 0.5,
                userSelect: 'none',
              }}
            >
              {label}
            </Typography>
          ) : null}
          {renderSelectedCard(value, 0)}
          <Typography
            component="button"
            type="button"
            className="nodrag nopan nowheel"
            onClick={handleShowPicker}
            onMouseDown={stopPropagation}
            onPointerDown={stopPropagation}
            sx={{
              mt: 0.75,
              p: 0,
              border: 0,
              background: 'none',
              fontSize: '11px',
              fontWeight: 500,
              color: 'primary.main',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Change agent
          </Typography>
        </Box>
      ) : (
        <NodeAutocomplete
          label={label}
          value={autocompleteValue}
          onChange={handleChange}
          options={agents}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, val) => option?.agentId === val?.agentId}
          renderOption={renderOption}
          placeholder={resolvedPlaceholder}
          loading={loading}
          loadingText={loadingText}
          noOptionsText={noOptionsText}
          disabled={disabled}
          inDialog={inDialog}
          canvas={canvas}
          multiple={multiple}
          disableCloseOnSelect={multiple || undefined}
          renderTags={hideInputTags ? () => [] : undefined}
        />
      )}
      {showCard && selectedAgents.length > 0 && !showCollapsedCard ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            mt: 1,
          }}
        >
          {selectedAgents.map((agent, index) => renderSelectedCard(agent, index))}
        </Box>
      ) : null}
    </>
  );
});

export default NodeAgentPicker;
